/**
 * Addon Purchase Service
 * Handles payment flows for purchasing additional services (crypto exchanges, banks, etc.)
 * after initial company formation payment.
 */

import api, { setAccessToken as setGlobalAccessToken } from './api';
import logger from '../utils/logger';

class AddonPurchaseService {
  constructor() {
    this.endpoints = {
      createIntent: '/api/addon-purchase/create-intent',
      status: '/api/addon-purchase/status',
      confirm: '/api/addon-purchase/confirm',
    };
  }

  /**
   * Set access token on the centralized Axios instance
   * @param {string} token - Auth0 access token
   */
  setAccessToken(token) {
    setGlobalAccessToken(token);
  }

  /**
   * Make an authenticated API request via the centralized Axios instance
   * @param {string} endpoint - Relative endpoint path
   * @param {string} method - HTTP method
   * @param {Object} [data] - Request body
   */
  async request(endpoint, method = 'GET', data = undefined) {
    const config = { method };
    if (data !== undefined) {
      config.data = data;
    }
    const response = await api(endpoint, config);
    return response.data;
  }

  // =========================================================================
  // ADDON PURCHASE METHODS
  // =========================================================================

  /**
   * Create a PaymentIntent for purchasing an addon service
   * @param {string} registrationId - The registration UUID
   * @param {string} addonType - Type of addon ('crypto_exchange', 'bank', 'payment_processor')
   * @param {string} providerId - Provider identifier ('kraken', 'mercury', 'relay', 'stripe')
   * @param {string} paymentMethodType - Payment method type ('card' or 'crypto')
   * @returns {Promise<Object>} { success, clientSecret, paymentIntentId, amount, currency, addonName, error }
   */
  async createAddonPaymentIntent(
    registrationId,
    addonType,
    providerId,
    paymentMethodType = 'card'
  ) {
    try {
      const result = await this.request(this.endpoints.createIntent, 'POST', {
        registrationId,
        addonType,
        providerId,
        paymentMethodType,
      });
      return result;
    } catch (error) {
      logger.error('Failed to create addon payment intent:', error);
      return {
        success: false,
        error: error.message || 'Failed to create payment intent',
      };
    }
  }

  /**
   * Get the status of an addon payment
   * @param {string} paymentIntentId - Stripe PaymentIntent ID
   * @returns {Promise<Object>} { success, addonType, providerId, message, error }
   */
  async getAddonPaymentStatus(paymentIntentId) {
    try {
      const result = await this.request(
        `${this.endpoints.status}/${paymentIntentId}`
      );
      return result;
    } catch (error) {
      logger.error('Failed to get addon payment status:', error);
      return {
        success: false,
        error: error.message || 'Failed to get payment status',
      };
    }
  }

  /**
   * Confirm addon payment (for manual confirmation if needed)
   * @param {string} paymentIntentId - Stripe PaymentIntent ID
   * @param {string} paymentMethodId - Optional payment method ID
   * @returns {Promise<Object>} { success, addonType, providerId, message, error }
   */
  async confirmAddonPayment(paymentIntentId, paymentMethodId = null) {
    try {
      const body = { paymentIntentId };
      if (paymentMethodId) {
        body.paymentMethodId = paymentMethodId;
      }
      const result = await this.request(this.endpoints.confirm, 'POST', body);
      return result;
    } catch (error) {
      logger.error('Failed to confirm addon payment:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm payment',
      };
    }
  }

  /**
   * Poll for payment completion
   * @param {string} paymentIntentId - Stripe PaymentIntent ID
   * @param {number} maxAttempts - Maximum polling attempts (default: 10)
   * @param {number} intervalMs - Polling interval in ms (default: 2000)
   * @returns {Promise<Object>} Final payment status
   */
  async pollPaymentStatus(
    paymentIntentId,
    maxAttempts = 10,
    intervalMs = 2000
  ) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const status = await this.getAddonPaymentStatus(paymentIntentId);

      if (status.success) {
        return status;
      }

      // If there's a specific error (not just "pending"), return immediately
      if (status.error && !status.message?.includes('pending')) {
        return status;
      }

      // Wait before next attempt
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    return {
      success: false,
      error: 'Payment confirmation timed out. Please refresh the page.',
    };
  }
}

// Export singleton instance
const addonPurchaseService = new AddonPurchaseService();
export default addonPurchaseService;

// Also export the class for testing
export { AddonPurchaseService };
